/**
 * Unused Strings Service
 * Finds strings that are not referenced by any object, dialog, or variable reference.
 * Provides functionality to deprecate or delete unreferenced strings in bulk.
 */

import { objectsStore } from '../stores/objectsStore';
import { getCachedStringsItems, type StringsItem } from '../stores/stringsStore';
import { getCachedDialogs } from '../stores/dialogs/cache/byGame';
import type { ObjectItem } from '../stores/objects/types';
import type { Dialog, DialogDataContent } from '../stores/dialogs/types';

// ============================================================================
// TYPES
// ============================================================================

export interface UnusedStringsReport {
  unusedActive: StringsItem[];
  unusedDeprecated: StringsItem[];
  totalStrings: number;
  referencedCount: number;
  timestamp: string;
  gameId: string;
}

export interface UnusedStringsProgress {
  stage: 'collecting' | 'comparing' | 'complete';
  progress: number;
  message: string;
}

// ============================================================================
// STRING ID COLLECTION — OBJECTS
// ============================================================================

function collectStringIdsFromObject(object: ObjectItem): string[] {
  const stringIds: string[] = [];

  for (const section of object.data) {
    for (const field of section.fields) {
      if (field.string_id && field.translation) {
        stringIds.push(field.string_id);
      }

      if (field.translation && field.multiple && Array.isArray(field.value)) {
        for (const item of field.value as any[]) {
          if (item && typeof item === 'object' && 'string_id' in item && item.string_id) {
            stringIds.push(item.string_id);
          }
        }
      }
    }
  }

  return stringIds;
}

// ============================================================================
// STRING ID COLLECTION — DIALOGS
// ============================================================================

function collectStringIdsFromDialogData(data: DialogDataContent | undefined): string[] {
  const stringIds: string[] = [];
  if (!data) return stringIds;

  // New structure: nodes
  if (data.nodes && Array.isArray(data.nodes)) {
    for (const node of data.nodes) {
      if (node.string_id) {
        stringIds.push(node.string_id);
      }
      if (node.options) {
        for (const option of node.options) {
          if (option.string_id) {
            stringIds.push(option.string_id);
          }
        }
      }
    }
  }

  // Legacy structure: sections → fields
  if (data.sections && Array.isArray(data.sections)) {
    for (const section of data.sections) {
      if (!section.fields) continue;
      for (const field of section.fields) {
        if (field.string_id && field.translatable) {
          stringIds.push(field.string_id);
        }
        if (field.options) {
          for (const option of field.options) {
            if (option.string_id && option.translatable) {
              stringIds.push(option.string_id);
            }
          }
        }
      }
    }
  }

  return stringIds;
}

// ============================================================================
// STRING ID COLLECTION — VARIABLE REFERENCES
// ============================================================================

/**
 * Collect string IDs that are cross-referenced by other strings via variable_references.
 */
function collectStringIdsFromVariableReferences(strings: StringsItem[]): string[] {
  const stringIds: string[] = [];

  for (const str of strings) {
    if (str.variable_references) {
      for (const ref of str.variable_references) {
        if (ref.stringId) {
          stringIds.push(ref.stringId);
        }
      }
    }
  }

  return stringIds;
}

// ============================================================================
// MAIN SERVICE
// ============================================================================

/**
 * Scan the game for strings that are not referenced by any entity.
 */
export function findUnusedStrings(
  gameId: string,
  onProgress?: (progress: UnusedStringsProgress) => void,
): UnusedStringsReport {
  onProgress?.({
    stage: 'collecting',
    progress: 10,
    message: 'Collecting string references from objects...',
  });

  const referencedIds = new Set<string>();

  // Collect from all objects (library objects + document-type objects)
  const objects = objectsStore.getData().filter(obj => obj.game_id === gameId);
  for (const obj of objects) {
    for (const id of collectStringIdsFromObject(obj)) {
      referencedIds.add(id);
    }
  }

  onProgress?.({
    stage: 'collecting',
    progress: 40,
    message: 'Collecting string references from dialogs...',
  });

  // Collect from all dialogs
  const dialogs: Dialog[] = getCachedDialogs(gameId);
  for (const dialog of dialogs) {
    for (const id of collectStringIdsFromDialogData(dialog.data)) {
      referencedIds.add(id);
    }
  }

  onProgress?.({
    stage: 'collecting',
    progress: 60,
    message: 'Collecting variable cross-references...',
  });

  // Collect cross-references between strings
  const allStrings = getCachedStringsItems(gameId);
  for (const id of collectStringIdsFromVariableReferences(allStrings)) {
    referencedIds.add(id);
  }

  onProgress?.({
    stage: 'comparing',
    progress: 80,
    message: 'Identifying unused strings...',
  });

  // Partition unreferenced strings into active vs already-deprecated
  const unusedActive: StringsItem[] = [];
  const unusedDeprecated: StringsItem[] = [];

  for (const str of allStrings) {
    if (!referencedIds.has(str.id)) {
      if (str.status === 'deprecated') {
        unusedDeprecated.push(str);
      } else {
        unusedActive.push(str);
      }
    }
  }

  onProgress?.({
    stage: 'complete',
    progress: 100,
    message: unusedActive.length + unusedDeprecated.length > 0
      ? `Found ${unusedActive.length} unused active and ${unusedDeprecated.length} deprecated strings`
      : 'All strings are referenced',
  });

  return {
    unusedActive,
    unusedDeprecated,
    totalStrings: allStrings.length,
    referencedCount: referencedIds.size,
    timestamp: new Date().toISOString(),
    gameId,
  };
}
